import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Zap, Users, MapPin, FileSearch, BarChart3, Globe, ArrowRight, Check } from 'lucide-react';

function ProductsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');

  const products = [
    {
      id: 'verifypro',
      name: 'VerifyPro',
      tagline: 'Enterprise Identity Verification',
      icon: Shield,
      category: 'enterprise',
      description: 'Comprehensive API orchestration for large-scale identity verification across Africa',
      features: [
        'Multi-country ID verification (Nigeria, Kenya, Ghana, South Africa)',
        'NIN, BVN, Passport, Driver License verification',
        'Real-time biometric liveness detection',
        'Fraud & risk analytics',
        'Custom workflow builder',
        'White-label options',
        'Dedicated account management',
        '99.9% SLA guarantee'
      ],
      useCases: [
        'Fintech & Banking',
        'Insurance companies',
        'Government agencies',
        'Large enterprises'
      ],
      pricing: 'Custom enterprise pricing',
      color: 'purple'
    },
    {
      id: 'hirecheck',
      name: 'HireCheck',
      tagline: 'HR & Employment Verification',
      icon: Users,
      category: 'smb',
      description: 'All-in-one platform for SMBs to verify employees, contractors and candidates',
      features: [
        'Employment history verification',
        'Education & credential checks',
        'Reference verification',
        'Criminal background checks',
        'Drug testing coordination',
        'Compliance tracking',
        'Simple dashboard',
        'Bulk upload support'
      ],
      useCases: [
        'HR departments',
        'Recruitment agencies',
        'Small & medium businesses',
        'Startups'
      ],
      pricing: 'From ₦25,000/month',
      color: 'blue'
    },
    {
      id: 'remoteverify',
      name: 'RemoteVerify',
      tagline: 'Remote Agent Verification Network',
      icon: MapPin,
      category: 'field',
      description: 'Physical verification services through our remote agent network across Africa',
      features: [
        'Address verification with GPS',
        'Asset verification (vehicles, property)',
        'On-site photo & video capture',
        'Timestamped evidence collection',
        '24-48 hour turnaround',
        'Quality scored agents',
        'Real-time tracking',
        'Audit trail'
      ],
      useCases: [
        'Lenders & financial institutions',
        'Insurance claims',
        'Logistics companies',
        'Real estate verification'
      ],
      pricing: 'From ₦2,500 per visit',
      color: 'green'
    },
    {
      id: 'verify',
      name: 'Instant Verify',
      tagline: 'Quick Digital Verification',
      icon: Zap,
      category: 'smb',
      description: 'Fast, affordable digital verification for businesses that need speed',
      features: [
        'Sub-2 second ID verification',
        'Email & phone verification',
        'Basic document checks',
        'Simple API integration',
        'Pay-as-you-go pricing',
        'No minimum commitment',
        'Self-service portal',
        'Instant results'
      ],
      useCases: [
        'E-commerce platforms',
        'Gig economy platforms',
        'SaaS products',
        'Marketplaces'
      ],
      pricing: 'From ₦150 per check',
      color: 'yellow'
    },
    {
      id: 'analytics',
      name: 'Fraud Analytics',
      tagline: 'AI-Powered Risk Intelligence',
      icon: BarChart3,
      category: 'enterprise',
      description: 'Advanced fraud detection and risk scoring using identity graphs and behavioral analytics',
      features: [
        'Real-time anomaly detection',
        'Identity graph linking',
        'Device fingerprinting',
        'Synthetic identity detection',
        'Risk scoring (0-1000)',
        'Custom rule engine',
        'Alert & monitoring dashboards',
        'Threat intelligence feed'
      ],
      useCases: [
        'Fintech platforms',
        'Online lenders',
        'Payment processors',
        'High-risk industries'
      ],
      pricing: 'Add-on from ₦50/check',
      color: 'red'
    },
    {
      id: 'global',
      name: 'Global Verify',
      tagline: 'Pan-African Coverage',
      icon: Globe,
      category: 'enterprise',
      description: 'Single API for identity verification across 10+ African countries',
      features: [
        'Unified API across countries',
        'Harmonized data schema',
        'Local regulatory compliance',
        'Multi-language support',
        'Local payment methods',
        'Regional data residency',
        'Country-specific datasets',
        'Fallback mechanisms'
      ],
      useCases: [
        'Pan-African businesses',
        'Regional banks',
        'Telco operators',
        'Multi-country platforms'
      ],
      pricing: 'Enterprise custom',
      color: 'indigo'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Products' },
    { id: 'enterprise', label: 'Enterprise' },
    { id: 'smb', label: 'SMB & Startups' },
    { id: 'field', label: 'Field Services' }
  ];

  const comparisonTable = [
    { feature: 'ID Verification', verifypro: true, hirecheck: true, remoteverify: false, verify: true },
    { feature: 'Biometric Liveness', verifypro: true, hirecheck: true, remoteverify: false, verify: false },
    { feature: 'Address Verification', verifypro: true, hirecheck: false, remoteverify: true, verify: false },
    { feature: 'Field Agent Network', verifypro: true, hirecheck: false, remoteverify: true, verify: false },
    { feature: 'Employment History', verifypro: true, hirecheck: true, remoteverify: false, verify: false },
    { feature: 'Fraud Analytics', verifypro: true, hirecheck: false, remoteverify: false, verify: false },
    { feature: 'Multi-Country', verifypro: true, hirecheck: false, remoteverify: true, verify: false },
    { feature: 'Custom Workflows', verifypro: true, hirecheck: false, remoteverify: false, verify: false },
    { feature: 'White-Label', verifypro: true, hirecheck: false, remoteverify: false, verify: false },
    { feature: 'SLA Guarantee', verifypro: '99.9%', hirecheck: '99.5%', remoteverify: '99.5%', verify: '99.5%' }
  ];

  const filteredProducts = activeTab === 'all' 
    ? products 
    : products.filter(p => p.category === activeTab);

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-600',
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      red: 'bg-red-100 text-red-600',
      indigo: 'bg-indigo-100 text-indigo-600'
    };
    return colors[color] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <img 
              src="/hakicheck-logo.png" 
              alt="HakiCheck" 
              className="h-12 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/pricing')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/status')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Status
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-2 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-medium rounded-lg transition-colors"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Products Built for Every Business
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            From startups to enterprises. Choose the right verification solution for your needs.
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 justify-center flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === cat.id
                    ? 'bg-[#5a738f] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => {
              const Icon = product.icon;
              return (
                <div
                  key={product.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1"
                >
                  <div className="p-8">
                    <div className={`inline-flex p-4 rounded-xl ${getColorClasses(product.color)} mb-4`}>
                      <Icon size={32} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{product.tagline}</p>
                    <p className="text-gray-700 mb-6">{product.description}</p>

                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                      <ul className="space-y-2">
                        {product.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                            <Check size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {product.features.length > 4 && (
                        <p className="text-sm text-gray-500 mt-2">+ {product.features.length - 4} more features</p>
                      )}
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                      <div className="flex flex-wrap gap-2">
                        {product.useCases.map((useCase, idx) => (
                          <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="pt-6 border-t border-gray-200 flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600">Starting at</div>
                        <div className="text-lg font-bold text-gray-900">{product.pricing}</div>
                      </div>
                      <button
                        onClick={() => navigate('/auth')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-semibold rounded-lg transition-colors"
                      >
                        Get Started
                        <ArrowRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Product Comparison</h2>
            <p className="text-xl text-gray-600">Find the right product for your use case</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl overflow-hidden shadow-lg">
              <thead className="bg-[#5a738f] text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Feature</th>
                  <th className="px-6 py-4 text-center font-semibold">VerifyPro</th>
                  <th className="px-6 py-4 text-center font-semibold">HireCheck</th>
                  <th className="px-6 py-4 text-center font-semibold">RemoteVerify</th>
                  <th className="px-6 py-4 text-center font-semibold">Instant Verify</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comparisonTable.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{row.feature}</td>
                    <td className="px-6 py-4 text-center">
                      {typeof row.verifypro === 'boolean' ? (
                        row.verifypro ? <Check size={20} className="mx-auto text-green-600" /> : <span className="text-gray-300">—</span>
                      ) : (
                        <span className="text-sm text-gray-900">{row.verifypro}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {typeof row.hirecheck === 'boolean' ? (
                        row.hirecheck ? <Check size={20} className="mx-auto text-green-600" /> : <span className="text-gray-300">—</span>
                      ) : (
                        <span className="text-sm text-gray-900">{row.hirecheck}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {typeof row.remoteverify === 'boolean' ? (
                        row.remoteverify ? <Check size={20} className="mx-auto text-green-600" /> : <span className="text-gray-300">—</span>
                      ) : (
                        <span className="text-sm text-gray-900">{row.remoteverify}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {typeof row.verify === 'boolean' ? (
                        row.verify ? <Check size={20} className="mx-auto text-green-600" /> : <span className="text-gray-300">—</span>
                      ) : (
                        <span className="text-sm text-gray-900">{row.verify}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Use Case Guide */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Which Product is Right for You?</h2>
            <p className="text-xl text-gray-600">Quick guide to help you choose</p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Choose VerifyPro if:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• You need enterprise-grade verification at scale (10,000+ checks/month)</li>
                <li>• You operate in multiple African countries</li>
                <li>• You require custom workflows and white-label options</li>
                <li>• Fraud prevention is mission-critical</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Choose HireCheck if:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• You're an HR team or recruitment agency</li>
                <li>• You need employee background screening</li>
                <li>• You want a simple, all-in-one dashboard</li>
                <li>• Budget is ₦25,000-₦100,000/month</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Choose RemoteVerify if:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• You need physical address verification via remote agents</li>
                <li>• Asset verification is required (vehicles, property)</li>
                <li>• Digital data isn't enough for your risk assessment</li>
                <li>• You're in lending, insurance, or logistics</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Instant Verify if:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• You're a startup or small business</li>
                <li>• You need basic ID verification quickly</li>
                <li>• Pay-as-you-go pricing works best for you</li>
                <li>• Speed is more important than depth</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#5a738f] to-[#4a5f7f] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Still Not Sure?</h2>
          <p className="text-xl mb-8 opacity-90">Let us help you choose the right product for your business</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/auth')}
              className="px-8 py-4 bg-white text-[#5a738f] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Free Trial
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#5a738f] transition-colors"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 HakiCheck. Trust Infrastructure for Africa.</p>
        </div>
      </footer>
    </div>
  );
}

export default ProductsPage;
